import { useMemo } from 'react';
import { NavLink, useLocation, useSearchParams } from 'react-router-dom';
import { styled, type Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Role } from '@bloomays-lib/types.shared';
import { useAuthStore } from '../../stores';
import {
  resolvePrimesPeriodToggleProps,
  type PrimesPeriodToggleProps,
  type PrimesPeriodView,
} from '../../utils/primesPeriodToggle.utils';

export type { PrimesPeriodToggleProps, PrimesPeriodView };

type ItemStyleProps = { theme: Theme; $active: boolean };

const periodToggleItemStyles = ({ theme, $active }: ItemStyleProps) => ({
  padding: `${theme.spacing(0.75)} ${theme.spacing(2)}`,
  borderRadius: 8,
  border: `1px solid ${theme.palette.neutral[20]}`,
  backgroundColor: theme.palette.neutral.white,
  fontFamily: '"Poppins SemiBold", sans-serif',
  fontSize: '13px',
  fontWeight: 600,
  color: theme.palette.neutral[60],
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  textDecoration: 'none',
  ...($active && {
    color: theme.palette.red[60],
    borderColor: theme.palette.red[40],
    backgroundColor: theme.palette.red[20],
  }),
});

const PrimesPeriodToggle = ({ activeView, tamEmail, selectedYear, referenceDate }: PrimesPeriodToggleProps) => {
  const { t } = useTranslation(['primes']);
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const isSystem = useAuthStore((state) => state.auth?.user?.roles?.includes(Role.System) ?? false);

  const resolvedProps = useMemo(
    () =>
      resolvePrimesPeriodToggleProps({
        pathname,
        searchParams,
        isSystem,
        activeView,
        tamEmail,
        selectedYear,
        referenceDate,
      }),
    [activeView, isSystem, pathname, referenceDate, searchParams, selectedYear, tamEmail],
  );

  if (!resolvedProps) {
    return null;
  }

  const monthlyActive = resolvedProps.value === 'monthly';
  const annualActive = resolvedProps.value === 'annual';

  return (
    <ToggleContainer aria-label={t('periodToggleAriaLabel')}>
      <ToggleNavItem to={resolvedProps.monthlyTo} $active={monthlyActive}>
        {t('navMonthly')}
      </ToggleNavItem>
      <ToggleNavItem to={resolvedProps.annualTo} $active={annualActive}>
        {t('navAnnual')}
      </ToggleNavItem>
    </ToggleContainer>
  );
};

export default PrimesPeriodToggle;

const toggleItemStyles = {
  shouldForwardProp: (prop: string) => prop !== '$active',
};

const ToggleContainer = styled('nav')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const ToggleNavItem = styled(NavLink, toggleItemStyles)<{ $active: boolean }>(periodToggleItemStyles);
